import { Component, ChangeDetectionStrategy, ElementRef, effect, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { DijuChatService, DijuPendingAction } from '../../../core/services/diju-chat.service';
import { AuthService } from '../../../core/services/auth.service';
import { RoleService } from '../../../core/services/role.service';
import { UserService } from '../../../core/services/user.service';
import { FeatureService } from '../../../core/services/permission.service';
import { ModuleService } from '../../../core/services/module.service';
import { DepartmentService } from '../../../core/services/department.service';
import { EffectiveUserPermissions, Feature } from '../../../core/models/permission.model';

interface QuickAction {
  label: string;
  onClick: () => void;
}

interface StatItem { label: string; value: number; icon: string; }
interface PermRow { name: string; allowed: boolean; }
interface PermGroupRow { featureName: string; perms: PermRow[]; }

type DijuCard =
  | { kind: 'status'; heading?: string; icon: string; title: string; subtitle: string; tone: 'success' | 'danger'; warning?: string }
  | { kind: 'stats'; heading: string; items: StatItem[] }
  | { kind: 'permlist'; heading: string; groups: PermGroupRow[] };

interface DijuMessage {
  id: number;
  from: 'user' | 'diju';
  text?: string;
  card?: DijuCard;
  pendingAction?: DijuPendingAction | null;
  resolved?: boolean;
  quickActions?: QuickAction[];
}

interface PickOption {
  id: string;
  label: string;
}

interface PermissionOption extends PickOption {
  featureId: string;
}

type FlowKind = 'user-perm' | 'role-perm' | 'user-all-perms';

interface ActiveFlow {
  kind: FlowKind;
  step: 1 | 2;
  loading: boolean;
  entityLabel: string;
  entityOptions: PickOption[];
  selectedEntityId: string;
  permissionOptions: PermissionOption[];
  features: Feature[];
}

let nextMessageId = 1;

@Component({
  selector: 'rps-diju-chat-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  styles: [`
    :host { display: block; }

    .diju-fab {
      position: fixed;
      bottom: 1.5rem;
      right: 1.5rem;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.35);
      padding: 0;
      cursor: pointer;
      overflow: hidden;
      background: linear-gradient(135deg, #060e1f 0%, #0d1f45 40%, #0e2d72 75%, #1a3a8a 100%);
      box-shadow: 0 8px 24px rgba(15,23,42,.35);
      /* Below .drawer-backdrop/.drawer (1040/1050 in styles.css) so the add/create
         drawers always sit above DIJU instead of it floating over their forms. */
      z-index: 1020;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform .15s ease;
    }
    .diju-fab:hover { transform: scale(1.06); }
    .diju-fab img { width: 100%; height: 100%; object-fit: cover; }

    .diju-panel {
      position: fixed;
      bottom: 5.5rem;
      right: 1.5rem;
      width: 360px;
      max-width: calc(100vw - 2rem);
      height: 90vh;
      max-height: calc(100vh - 8rem);
      display: flex;
      flex-direction: column;
      border-radius: 20px;
      overflow: hidden;
      background: #ffffff;
      box-shadow: 0 16px 48px rgba(15,23,42,.25);
      z-index: 1021;
      animation: dijuPanelIn .16s ease;
    }
    @keyframes dijuPanelIn { from { opacity:0; transform: translateY(12px); } to { opacity:1; transform: translateY(0); } }

    /* Same gradient as the .rps-banner header card used on every page, so the
       widget's header reads as part of the same design system. */
    .diju-header {
      display: flex;
      align-items: center;
      gap: .6rem;
      padding: .85rem 1rem;
      background: linear-gradient(135deg, #060e1f 0%, #0d1f45 40%, #0e2d72 75%, #1a3a8a 100%);
    }
    .diju-header img { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(255,255,255,0.3); }
    .diju-header .title { color: #fff; font-weight: 600; font-size: 14.5px; line-height: 1.1; }
    .diju-header .subtitle { color: rgba(255,255,255,0.68); font-size: 11.5px; }
    .diju-header .close-btn {
      margin-left: auto;
      background: rgba(255,255,255,0.14);
      border: none;
      color: #fff;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      cursor: pointer;
      font-size: 12px;
    }
    .diju-header .close-btn:hover { background: rgba(255,255,255,0.26); }

    .diju-messages {
      flex: 1;
      overflow-y: auto;
      padding: 1rem .9rem;
      display: flex;
      flex-direction: column;
      gap: .7rem;
      background: #ffffff;
    }
    .diju-msg { display: flex; gap: .5rem; align-items: flex-end; }
    .diju-msg--user { justify-content: flex-end; }
    .diju-msg--diju { justify-content: flex-start; }
    .diju-msg--user .diju-avatar { order: 1; }
    .diju-avatar {
      width: 22px; height: 22px; border-radius: 50%; object-fit: cover;
      flex-shrink: 0; border: 1px solid #e2e8f0;
    }
    .diju-bubble {
      max-width: 78%;
      padding: .6rem .8rem;
      border-radius: 14px;
      font-size: 13.2px;
      line-height: 1.45;
      white-space: pre-wrap;
    }
    .diju-msg--user .diju-bubble { background: linear-gradient(135deg, #2f6fed, #1d4ed8); color: #fff; border-bottom-right-radius: 4px; }
    .diju-msg--diju .diju-bubble { background: #f1f5f9; color: #1e293b; border: 1px solid #e2e8f0; border-bottom-left-radius: 4px; }
    .diju-bubble--card { padding: .7rem .8rem; }

    .card-heading { font-weight: 700; font-size: 12.5px; letter-spacing: .2px; color: #1d4ed8; margin-bottom: .55rem; }

    .status-row { display: flex; align-items: flex-start; gap: .55rem; padding: .1rem 0; }
    .status-icon { font-size: 20px; line-height: 1; }
    .status-title { font-weight: 700; font-size: 13.5px; color: #0f172a; }
    .status-subtitle { font-size: 12px; margin-top: .15rem; }
    .status-row.tone-success .status-subtitle { color: #15803d; font-weight: 600; }
    .status-row.tone-danger .status-subtitle { color: #dc2626; font-weight: 600; }
    .card-warning {
      margin-top: .55rem;
      padding: .5rem .6rem;
      border-radius: 10px;
      background: #fff7ed;
      border: 1px solid #fbbf24;
      color: #92400e;
      font-size: 11.8px;
      line-height: 1.4;
    }

    .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .5rem; min-width: 220px; }
    .stat-tile {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: .55rem .5rem;
      text-align: center;
    }
    .stat-icon { font-size: 16px; }
    .stat-value { font-size: 17px; font-weight: 800; color: #1d4ed8; line-height: 1.3; }
    .stat-label { font-size: 10.5px; color: #64748b; text-transform: uppercase; letter-spacing: .3px; }

    .permlist { display: flex; flex-direction: column; gap: .6rem; min-width: 220px; }
    .permlist-group { }
    .permlist-feature { font-weight: 700; font-size: 12.3px; color: #1e293b; margin-bottom: .3rem; }
    .permlist-perms { display: flex; flex-wrap: wrap; gap: .3rem; }
    .perm-chip {
      font-size: 11px;
      font-weight: 600;
      padding: .18rem .5rem;
      border-radius: 999px;
      border: 1px solid transparent;
    }
    .perm-chip.chip-on { background: #dcfce7; color: #15803d; border-color: #86efac; }
    .perm-chip.chip-off { background: #f1f5f9; color: #94a3b8; border-color: #e2e8f0; }

    .diju-actions { display: flex; gap: .5rem; margin: -.1rem 0 .1rem 1.75rem; }
    .diju-actions button {
      border: none;
      border-radius: 8px;
      padding: .4rem .85rem;
      font-size: 12.5px;
      font-weight: 700;
      cursor: pointer;
    }
    .diju-actions .confirm-btn { background: #22c55e; color: #06210f; }
    .diju-actions .confirm-btn:hover { background: #16a34a; }
    .diju-actions .cancel-btn { background: #f1f5f9; color: #334155; border: 1px solid #e2e8f0; }
    .diju-actions .cancel-btn:hover { background: #e2e8f0; }

    .diju-actions--menu { flex-direction: column; align-items: stretch; max-width: 78%; gap: .4rem; }
    .diju-actions--menu .menu-btn {
      width: 100%;
      text-align: left;
      background: #f8fafc;
      color: #1e293b;
      border: 1px solid #e2e8f0;
      padding: .5rem .7rem;
      border-radius: 10px;
      font-weight: 600;
    }
    .diju-actions--menu .menu-btn:hover { background: #eef2ff; border-color: #93b4fa; }

    .diju-typing { color: #94a3b8; font-size: 12px; padding-left: 1.75rem; }

    .diju-picker {
      display: flex;
      flex-direction: column;
      gap: .45rem;
      padding: .65rem .9rem;
      border-top: 1px solid #e5e7eb;
      background: #eef4ff;
    }
    .diju-picker-head { display: flex; align-items: center; gap: .5rem; }
    .diju-picker-label { color: #3b5bdb; font-size: 11.5px; font-weight: 700; white-space: nowrap; text-transform: uppercase; letter-spacing: .3px; flex: 1; }
    .diju-picker-cancel {
      background: #e2e8f0;
      border: none;
      color: #475569;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      cursor: pointer;
      font-size: 10.5px;
      flex-shrink: 0;
    }
    .diju-picker-cancel:hover { background: #cbd5e1; }
    .diju-picker-search {
      border: 1px solid #bcd0fb;
      background: #ffffff;
      color: #1e293b;
      border-radius: 8px;
      padding: .45rem .6rem;
      font-size: 12.5px;
      outline: none;
    }
    .diju-picker-search::placeholder { color: #94a3b8; }
    .diju-picker-search:focus { border-color: #2f6fed; }
    .diju-picker-list {
      max-height: 150px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: .2rem;
      background: #ffffff;
      border: 1px solid #dbe6fb;
      border-radius: 8px;
      padding: .3rem;
    }
    .picker-option {
      text-align: left;
      background: transparent;
      border: none;
      color: #1e293b;
      padding: .4rem .5rem;
      border-radius: 6px;
      font-size: 12.5px;
      cursor: pointer;
    }
    .picker-option:hover { background: #eef2ff; }
    .picker-empty { padding: .5rem; color: #94a3b8; font-size: 12px; text-align: center; }

    .diju-input-row {
      display: flex;
      gap: .5rem;
      padding: .7rem;
      border-top: 1px solid #e5e7eb;
      background: #ffffff;
    }
    .diju-input-row .menu-icon-btn {
      background: #ffffff;
      border: 1px solid #cbd5e1;
      color: #1d4ed8;
      border-radius: 10px;
      width: 38px;
      cursor: pointer;
    }
    .diju-input-row .menu-icon-btn:disabled { opacity: .5; cursor: default; }
    .diju-input-row .menu-icon-btn:not(:disabled):hover { background: #eef2ff; border-color: #93b4fa; }
    .diju-input-row input {
      flex: 1;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #1e293b;
      border-radius: 10px;
      padding: .5rem .7rem;
      font-size: 13px;
      outline: none;
    }
    .diju-input-row input::placeholder { color: #94a3b8; }
    .diju-input-row input:focus { border-color: #2f6fed; }
    .diju-input-row button {
      background: #ffffff;
      border: 1px solid #cbd5e1;
      color: #1d4ed8;
      border-radius: 10px;
      width: 38px;
      cursor: pointer;
    }
    .diju-input-row button:disabled { opacity: .5; cursor: default; }
    .diju-input-row button:not(:disabled):hover { background: #eef2ff; border-color: #93b4fa; }
  `],
  template: `
    @if (open()) {
      <div class="diju-panel">
        <div class="diju-header">
          <img src="images/DIJU2.jpg" alt="DIJU" />
          <div>
            <div class="title">DIJU</div>
            <div class="subtitle">Permission lookup assistant</div>
          </div>
          <button class="close-btn" (click)="toggle()" aria-label="Close">✕</button>
        </div>

        <div class="diju-messages" #messagesEl>
          @for (msg of messages(); track msg.id) {
            <div class="diju-msg" [class.diju-msg--user]="msg.from === 'user'" [class.diju-msg--diju]="msg.from === 'diju'">
              @if (msg.from === 'diju') {
                <img class="diju-avatar" src="images/DIJU2.jpg" alt="" />
              } @else {
                <img class="diju-avatar" [src]="userAvatarUrl()" alt="" />
              }
              <div class="diju-bubble" [class.diju-bubble--card]="msg.card">
                @if (msg.card; as card) {
                  @if (card.heading) {
                    <div class="card-heading">{{ card.heading }}</div>
                  }
                  @switch (card.kind) {
                    @case ('status') {
                      <div class="status-row" [class.tone-success]="card.tone === 'success'" [class.tone-danger]="card.tone === 'danger'">
                        <span class="status-icon">{{ card.icon }}</span>
                        <div>
                          <div class="status-title">{{ card.title }}</div>
                          <div class="status-subtitle">{{ card.subtitle }}</div>
                        </div>
                      </div>
                      @if (card.warning) {
                        <div class="card-warning">⚠️ {{ card.warning }}</div>
                      }
                    }
                    @case ('stats') {
                      <div class="stats-grid">
                        @for (item of card.items; track item.label) {
                          <div class="stat-tile">
                            <div class="stat-icon">{{ item.icon }}</div>
                            <div class="stat-value">{{ item.value }}</div>
                            <div class="stat-label">{{ item.label }}</div>
                          </div>
                        }
                      </div>
                    }
                    @case ('permlist') {
                      <div class="permlist">
                        @for (g of card.groups; track g.featureName) {
                          <div class="permlist-group">
                            <div class="permlist-feature">{{ g.featureName }}</div>
                            <div class="permlist-perms">
                              @for (p of g.perms; track p.name) {
                                <span class="perm-chip" [class.chip-on]="p.allowed" [class.chip-off]="!p.allowed">
                                  {{ p.allowed ? '✓' : '✗' }} {{ p.name }}
                                </span>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    }
                  }
                } @else {
                  {{ msg.text }}
                }
              </div>
            </div>
            @if (msg.pendingAction && !msg.resolved) {
              <div class="diju-actions">
                <button class="confirm-btn" (click)="confirmAction(msg)">✅ Confirm</button>
                <button class="cancel-btn" (click)="cancelAction(msg)">✕ Cancel</button>
              </div>
            }
            @if (msg.quickActions?.length) {
              <div class="diju-actions diju-actions--menu">
                @for (qa of msg.quickActions!; track qa.label) {
                  <button class="menu-btn" (click)="qa.onClick()">{{ qa.label }}</button>
                }
              </div>
            }
          }
          @if (loading()) {
            <div class="diju-typing">DIJU is typing…</div>
          }
        </div>

        @if (activeFlow(); as flow) {
          <div class="diju-picker">
            <div class="diju-picker-head">
              <span class="diju-picker-label">{{ flow.step === 1 ? flow.entityLabel : 'Permission' }}</span>
              <button class="diju-picker-cancel" (click)="cancelFlow()" aria-label="Cancel">✕</button>
            </div>
            @if (flow.loading) {
              <div class="diju-typing">Loading…</div>
            } @else {
              <input
                type="text"
                class="diju-picker-search"
                [placeholder]="'Type to search ' + (flow.step === 1 ? flow.entityLabel.toLowerCase() : 'permissions') + '…'"
                [(ngModel)]="pickerSearchText"
              />
              <div class="diju-picker-list">
                @for (opt of filteredPickerOptions(flow); track opt.id) {
                  <button type="button" class="picker-option" (click)="selectPickerOption(flow, opt.id)">{{ opt.label }}</button>
                } @empty {
                  <div class="picker-empty">No matches found</div>
                }
              </div>
            }
          </div>
        }

        <div class="diju-input-row">
          <button class="menu-icon-btn" (click)="showMenu()" [disabled]="loading()" aria-label="Quick actions menu">
            <i class="fas fa-list"></i>
          </button>
          <input
            type="text"
            placeholder="Does [Role] have [Permission]?"
            [(ngModel)]="draft"
            (keydown.enter)="send()"
            [disabled]="loading() || !!activeFlow()"
          />
          <button (click)="send()" [disabled]="loading() || !!activeFlow() || !draft.trim()" aria-label="Send">
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    }

    <button class="diju-fab" (click)="toggle()" aria-label="Open DIJU chat">
      <img src="images/DIJU2.jpg" alt="DIJU" />
    </button>
  `
})
export class DijuChatWidgetComponent {
  private diju = inject(DijuChatService);
  private auth = inject(AuthService);
  private roleSvc = inject(RoleService);
  private userSvc = inject(UserService);
  private featureSvc = inject(FeatureService);
  private moduleSvc = inject(ModuleService);
  private deptSvc = inject(DepartmentService);

  private messagesEl = viewChild<ElementRef<HTMLDivElement>>('messagesEl');

  open = signal(false);
  loading = signal(false);
  draft = '';
  pickerSearchText = '';
  activeFlow = signal<ActiveFlow | null>(null);
  messages = signal<DijuMessage[]>([
    {
      id: nextMessageId++,
      from: 'diju',
      text: '👋 Hi, I\'m DIJU — your permission lookup assistant.\n\nType it like this:\n"Does [Role] have [Permission]?"\n\nOr tap 📋 below for quick actions.'
    }
  ]);

  constructor() {
    // Keep the latest message (or the typing indicator) in view as the conversation grows.
    effect(() => {
      this.messages();
      this.loading();
      const el = this.messagesEl()?.nativeElement;
      if (el) queueMicrotask(() => { el.scrollTop = el.scrollHeight; });
    });
  }

  userAvatarUrl(): string {
    const name = this.auth.currentUser()?.userName ?? 'guest';
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name)}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`;
  }

  toggle() {
    const opening = !this.open();
    this.open.update(v => !v);
    if (opening && this.messages().length === 1) this.showMenu();
  }

  // ── Quick-actions menu ──────────────────────────────────────────────────

  showMenu() {
    this.activeFlow.set(null);
    this.pushDiju('👋 What would you like to do?', [
      { label: "🔍 Check a user's permission", onClick: () => this.startPickFlow('user-perm') },
      { label: "🔍 Check a role's permission", onClick: () => this.startPickFlow('role-perm') },
      { label: "📋 Show a user's full permissions", onClick: () => this.startPickFlow('user-all-perms') },
      { label: '📊 App stats', onClick: () => this.runAppStats() }
    ]);
  }

  startPickFlow(kind: FlowKind) {
    const entityLabel = kind === 'role-perm' ? 'Role' : 'User';
    this.pickerSearchText = '';
    this.activeFlow.set({ kind, step: 1, loading: true, entityLabel, entityOptions: [], selectedEntityId: '', permissionOptions: [], features: [] });

    if (kind === 'role-perm') {
      this.roleSvc.getAll().subscribe({
        next: roles => this.setEntityOptions(roles.map(r => ({ id: r.id, label: r.name }))),
        error: () => this.failFlow()
      });
    } else {
      this.userSvc.getAll().subscribe({
        next: users => this.setEntityOptions(users.map(u => ({ id: u.id, label: `${u.userName} (${u.email})` }))),
        error: () => this.failFlow()
      });
    }
  }

  filteredPickerOptions(flow: ActiveFlow): PickOption[] {
    const list = flow.step === 1 ? flow.entityOptions : flow.permissionOptions;
    const q = this.pickerSearchText.trim().toLowerCase();
    return q ? list.filter(o => o.label.toLowerCase().includes(q)) : list;
  }

  selectPickerOption(flow: ActiveFlow, id: string) {
    this.pickerSearchText = '';
    if (flow.step === 1) this.onEntitySelected(id);
    else this.onPermissionSelected(id);
  }

  onEntitySelected(entityId: string) {
    const flow = this.activeFlow();
    if (!flow || !entityId) return;
    const entity = flow.entityOptions.find(o => o.id === entityId);
    if (!entity) return;

    if (flow.kind === 'user-all-perms') {
      this.pushUser(`Show all permissions for ${entity.label}`);
      this.activeFlow.set(null);
      this.loading.set(true);
      this.featureSvc.getEffectivePermissions(entityId).subscribe({
        next: dto => { this.loading.set(false); this.pushDijuCard(this.buildPermListCard(dto)); },
        error: () => { this.loading.set(false); this.pushDiju('😕 Sorry, I couldn\'t load that user\'s permissions.'); }
      });
      return;
    }

    this.activeFlow.update(f => f ? { ...f, selectedEntityId: entityId, step: 2, loading: true } : f);
    this.featureSvc.getAll().subscribe({
      next: features => {
        const options: PermissionOption[] = features.flatMap(f => f.permissions.map(p => ({ id: p.id, label: `${f.name} — ${p.name}`, featureId: f.id })));
        this.activeFlow.update(fl => fl ? { ...fl, permissionOptions: options, features, loading: false } : fl);
      },
      error: () => this.failFlow()
    });
  }

  onPermissionSelected(permissionId: string) {
    const flow = this.activeFlow();
    if (!flow || !permissionId) return;
    const entity = flow.entityOptions.find(o => o.id === flow.selectedEntityId);
    const perm = flow.permissionOptions.find(o => o.id === permissionId);
    if (!entity || !perm) return;

    this.pushUser(`Does ${flow.entityLabel.toLowerCase()} "${entity.label}" have "${perm.label}"?`);
    this.activeFlow.set(null);
    this.loading.set(true);

    if (flow.kind === 'role-perm') {
      this.featureSvc.getRolePermissions(entity.id).subscribe({
        next: groups => this.showPermissionResult(flow, entity, perm, groups.flatMap(g => g.permissions).find(p => p.permissionId === permissionId)?.isAllowed ?? false),
        error: () => this.lookupFailed()
      });
    } else {
      this.featureSvc.getUserPermissions(entity.id).subscribe({
        next: groups => this.showPermissionResult(flow, entity, perm, groups.flatMap(g => g.permissions).find(p => p.permissionId === permissionId)?.isAllowed ?? false),
        error: () => this.lookupFailed()
      });
    }
  }

  applyToggle(kind: FlowKind, entityId: string, permissionId: string, nextAllowed: boolean, entityLabel: string, permLabel: string) {
    this.loading.set(true);
    const onDone = () => {
      this.loading.set(false);
      this.pushDiju(`✅ Done — "${permLabel}" is now ${nextAllowed ? 'ENABLED' : 'DISABLED'} for "${entityLabel}".`);
    };
    const onError = () => {
      this.loading.set(false);
      this.pushDiju('😕 Sorry, I couldn\'t apply that change. Please try again.');
    };

    if (kind === 'role-perm') {
      this.featureSvc.updateRolePermission(entityId, permissionId, nextAllowed).subscribe({ next: onDone, error: onError });
    } else {
      this.featureSvc.updateUserPermission(entityId, permissionId, nextAllowed).subscribe({ next: onDone, error: onError });
    }
  }

  runAppStats() {
    this.pushUser('Show app stats');
    this.loading.set(true);
    forkJoin({
      departments: this.deptSvc.getAll(),
      roles: this.roleSvc.getAll(),
      users: this.userSvc.getAll(),
      modules: this.moduleSvc.getAll(),
      features: this.featureSvc.getAll()
    }).subscribe({
      next: r => {
        this.loading.set(false);
        const permissionCount = r.features.reduce((sum, f) => sum + f.permissions.length, 0);
        this.pushDijuCard({
          kind: 'stats',
          heading: '📊 App Stats',
          items: [
            { label: 'Departments', value: r.departments.length, icon: '🏢' },
            { label: 'Roles', value: r.roles.length, icon: '🎭' },
            { label: 'Users', value: r.users.length, icon: '👤' },
            { label: 'Modules', value: r.modules.length, icon: '🧩' },
            { label: 'Features', value: r.features.length, icon: '🗂️' },
            { label: 'Permissions', value: permissionCount, icon: '🔑' }
          ]
        });
      },
      error: () => { this.loading.set(false); this.pushDiju('😕 Sorry, I couldn\'t load the stats.'); }
    });
  }

  cancelFlow() {
    this.pickerSearchText = '';
    this.activeFlow.set(null);
    this.pushDiju('👍 Okay, cancelled.');
  }

  private setEntityOptions(options: PickOption[]) {
    this.activeFlow.update(f => f ? { ...f, entityOptions: options, loading: false } : f);
  }

  private failFlow() {
    this.activeFlow.set(null);
    this.pushDiju('😕 Sorry, I couldn\'t reach the server. Please try again.');
  }

  private lookupFailed() {
    this.loading.set(false);
    this.pushDiju('😕 Sorry, I couldn\'t check that. Please try again.');
  }

  private showPermissionResult(flow: ActiveFlow, entity: PickOption, perm: PermissionOption, allowed: boolean) {
    this.loading.set(false);

    // Disabling a permission here only touches this feature's own row — a child feature
    // (ParentFeatureId) with the same permission code keeps whatever value it already had,
    // so flag it instead of silently leaving the tree in a state the Permission Management
    // screen would otherwise enforce automatically on save.
    let warning: string | undefined;
    if (allowed) {
      const feature = flow.features.find(f => f.id === perm.featureId);
      const children = flow.features.filter(f => f.parentFeatureId === perm.featureId);
      if (children.length > 0) {
        const names = children.map(c => c.name).join(', ');
        warning = `"${feature?.name ?? 'This feature'}" has ${children.length} sub-feature${children.length > 1 ? 's' : ''} (${names}). Turning this off here won't automatically disable their matching permission — update those separately if needed.`;
      }
    }

    this.pushDijuCard(
      {
        kind: 'status',
        icon: allowed ? '✅' : '🚫',
        title: perm.label,
        subtitle: `Currently ${allowed ? 'ENABLED' : 'DISABLED'} for ${flow.entityLabel.toLowerCase()} "${entity.label}"`,
        tone: allowed ? 'success' : 'danger',
        warning
      },
      [{ label: `${allowed ? '🚫 Disable' : '✅ Enable'} it`, onClick: () => this.applyToggle(flow.kind, entity.id, perm.id, !allowed, entity.label, perm.label) }]
    );
  }

  private buildPermListCard(dto: EffectiveUserPermissions): DijuCard {
    return {
      kind: 'permlist',
      heading: `📋 Permissions — ${dto.userName}${dto.userRoleName ? ' · ' + dto.userRoleName : ''}`,
      groups: dto.permissions.map(f => ({
        featureName: f.featureName,
        perms: f.permissions.map(p => ({ name: p.name, allowed: p.isAllowed }))
      }))
    };
  }

  private pushUser(text: string) {
    this.messages.update(m => [...m, { id: nextMessageId++, from: 'user', text }]);
  }

  private pushDiju(text: string, quickActions?: QuickAction[]) {
    this.messages.update(m => [...m, { id: nextMessageId++, from: 'diju', text, quickActions }]);
  }

  private pushDijuCard(card: DijuCard, quickActions?: QuickAction[]) {
    this.messages.update(m => [...m, { id: nextMessageId++, from: 'diju', card, quickActions }]);
  }

  send() {
    const text = this.draft.trim();
    if (!text || this.loading()) return;

    this.messages.update(m => [...m, { id: nextMessageId++, from: 'user', text }]);
    this.draft = '';
    this.loading.set(true);

    this.diju.query(text).subscribe({
      next: res => {
        this.messages.update(m => [...m, {
          id: nextMessageId++,
          from: 'diju',
          text: res.reply,
          pendingAction: res.pendingAction
        }]);
        this.loading.set(false);
      },
      error: () => {
        this.messages.update(m => [...m, { id: nextMessageId++, from: 'diju', text: '😕 Sorry, something went wrong reaching the server.' }]);
        this.loading.set(false);
      }
    });
  }

  confirmAction(msg: DijuMessage) {
    if (!msg.pendingAction) return;
    const token = msg.pendingAction.confirmationToken;
    this.markResolved(msg.id);

    this.diju.confirm(token).subscribe({
      next: res => {
        this.messages.update(m => [...m, { id: nextMessageId++, from: 'diju', text: `${res.success ? '✅' : '⚠️'} ${res.message}` }]);
      },
      error: () => {
        this.messages.update(m => [...m, { id: nextMessageId++, from: 'diju', text: '😕 Sorry, I couldn\'t complete that change. Please try again.' }]);
      }
    });
  }

  cancelAction(msg: DijuMessage) {
    this.markResolved(msg.id);
    this.messages.update(m => [...m, { id: nextMessageId++, from: 'diju', text: '👍 Okay, cancelled.' }]);
  }

  private markResolved(id: number) {
    this.messages.update(m => m.map(msg => msg.id === id ? { ...msg, resolved: true } : msg));
  }
}
